import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../api/templates";

export function TemplatesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["templates"], queryFn: listTemplates });
  const [editingId, setEditingId] = useState<number | "new" | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading templates…</p>;
  const templates = data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Email Templates</h1>
        <button
          onClick={() => setEditingId("new")}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm"
        >
          New template
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Subject</th>
              <th className="px-4 py-2">Description</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {templates.map((t) => (
              <tr key={t.template_id}>
                <td className="px-4 py-2 font-medium">{t.name}</td>
                <td className="px-4 py-2 text-gray-600">{t.subject || "—"}</td>
                <td className="px-4 py-2 text-gray-500">{t.description || "—"}</td>
                <td className="px-4 py-2 text-right space-x-3">
                  <button onClick={() => setEditingId(t.template_id)} className="text-brand hover:underline">
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete template "${t.name}"?`)) remove.mutate(t.template_id);
                    }}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {templates.length === 0 && <p className="text-gray-400 text-sm p-4">No email templates yet.</p>}
      </div>

      {editingId !== null && (
        <Editor
          templateId={editingId === "new" ? null : editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            qc.invalidateQueries({ queryKey: ["templates"] });
          }}
        />
      )}
    </div>
  );
}

function Editor({
  templateId,
  onClose,
  onSaved,
}: {
  templateId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: existing, isLoading } = useQuery({
    queryKey: ["template", templateId],
    queryFn: () => getTemplate(templateId!),
    enabled: templateId !== null,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-full overflow-auto">
        {templateId !== null && isLoading ? (
          <p className="p-6 text-gray-500">Loading…</p>
        ) : (
          <EditorForm
            initial={
              existing ?? {
                template_id: 0,
                name: "",
                description: "",
                subject: "",
                body_html: "",
                body_text: "",
              }
            }
            templateId={templateId}
            onClose={onClose}
            onSaved={onSaved}
          />
        )}
      </div>
    </div>
  );
}

function EditorForm({
  initial,
  templateId,
  onClose,
  onSaved,
}: {
  initial: {
    name: string;
    description: string;
    subject: string;
    body_html: string | null;
    body_text: string | null;
  };
  templateId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [subject, setSubject] = useState(initial.subject);
  const [bodyHtml, setBodyHtml] = useState(initial.body_html ?? "");
  const [bodyText, setBodyText] = useState(initial.body_text ?? "");
  const [preview, setPreview] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description,
        subject,
        body_html: bodyHtml || null,
        body_text: bodyText || null,
      };
      return templateId ? updateTemplate(templateId, body) : createTemplate(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-lg font-bold">{templateId ? "Edit" : "New"} email template</h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Description</label>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Subject</label>
        <input
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-gray-500">HTML body</label>
          <button
            type="button"
            onClick={() => setPreview((p) => !p)}
            className="text-xs text-brand hover:underline"
          >
            {preview ? "Edit HTML" : "Preview"}
          </button>
        </div>
        {preview ? (
          // Sandboxed (no allow-scripts) so stored template HTML can't run JS in the operator's session.
          <iframe
            title="Template preview"
            sandbox=""
            srcDoc={bodyHtml}
            className="w-full border border-gray-200 rounded min-h-[10rem] bg-gray-50"
          />
        ) : (
          <textarea
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            rows={8}
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
          />
        )}
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Plain-text alternative</label>
        <textarea
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          rows={4}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
        />
      </div>
      <div className="flex gap-2 pt-2">
        <button
          disabled={!name || save.isPending}
          onClick={() => save.mutate()}
          className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
